import { lamportsToUiAmount, percentUiToDecimals } from "@bridgesplit/utils";
import { getZcLedger, getZcCollateral, isManageLeverageEligable, useUnwindQuoteAndIxs } from "@bridgesplit/abf-react";
import { DEFAULT_SLIPPAGE } from "app/components/common";
import { WindType } from "@loopscale/pandora-types";

import { UseFullRepayWithCollateralQuoteParams, UseFullRepayWithCollateralQuoteReturn } from "./types";

export function useFullRepayWithCollateralQuote({
    loanExpanded,
    slippagePercentDecimals
}: UseFullRepayWithCollateralQuoteParams): UseFullRepayWithCollateralQuoteReturn {
    const ledger = getZcLedger(loanExpanded);
    const collateral = getZcCollateral(loanExpanded);
    const manageLeverageEligable = isManageLeverageEligable(loanExpanded);

    // Full repay quote - determines max amount of collateral needed to close debt
    const {
        data: fullRepayWithCollateralQuote,
        error: fullRepayWithCollateralQuoteError,
        isLoading: fullRepayWithCollateralQuoteLoading,
        isFetching: fullRepayWithCollateralQuoteIsFetching
    } = useUnwindQuoteAndIxs({
        loanExpanded,
        debtToRepay: ledger?.ledgerDebt.total,
        loanCollateral: collateral,
        slippagePercentDecimals: slippagePercentDecimals ?? percentUiToDecimals(DEFAULT_SLIPPAGE),
        windType: WindType.Collateral,
        collateralAmount: collateral?.amount,
        skip: !manageLeverageEligable || slippagePercentDecimals === undefined,
        outputToken: loanExpanded?.principalMetadata,
        disablePoll: true
    });

    const fullRepayAmountCollateralUi = lamportsToUiAmount(
        fullRepayWithCollateralQuote?.quote.inputAmount ?? 0,
        collateral?.metadata.decimals
    );

    const fullRepayAmountInPrincipalFromCollateralRepaymentUi = lamportsToUiAmount(
        fullRepayWithCollateralQuote?.quote.minOutputAmount ?? 0,
        loanExpanded?.principalMetadata.decimals
    );

    return {
        fullRepayWithCollateralQuote,
        fullRepayWithCollateralQuoteLoading:
            fullRepayWithCollateralQuoteLoading || fullRepayWithCollateralQuoteIsFetching,
        fullRepayWithCollateralQuoteError: !!fullRepayWithCollateralQuoteError,
        fullRepayAmountCollateralUi,
        fullRepayAmountInPrincipalFromCollateralRepaymentUi
    };
}
